import {
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ItemPicker,
	CommonItemPickerOptions,
	ItemPickerOptions,
	SharePointItemPickerOptions,
	WebDavItemPickerOptions,
	ItemPickerLocation,
	ItemPickerMode
} from "../ItemPicker/ItemPicker";
import {
	convertToDestination,
	ThingsToMove
} from "client_legacy/Documents/MoveAndCopy/common";
import {
	DocumentStoreType,
	MoveFailedItem,
	MoveResult
} from "client/Api/Documents/common";
import { SharePointMoveImplementer } from "client_legacy/Documents/MoveAndCopy/sharePointMoveImplementer";
import { WebDavMoveImplementer } from "client_legacy/Documents/MoveAndCopy/webDavMoveImplementer";
import { AlertManager, useAlerts, useUnmountedCancellation } from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export type MoveDialogProps = {
	onClose: () => void;
	thingsToMove: ThingsToMove;
};

export const MoveDialog = (props: MoveDialogProps): ReactElement => {
	const {
		thingsToMove,
		onClose
	} = props;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [failedItemsDialogState, setFailedItemsDialogState] =
		useState<FailedItemsDialogState>({ isOpen: false });
	const [location, setLocation] = useState<ItemPickerLocation>(
		(): ItemPickerLocation => thingsToMove.source
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const isButtonDisabled: boolean =
		getLocationUniqueId(thingsToMove.source) === getLocationUniqueId(location);

	const sharePointMoveImplementer = useMemo(
		(): SharePointMoveImplementer => {
			return new SharePointMoveImplementer();
		},
		[]
	);

	const webDavMoveImplementer = useMemo(
		(): WebDavMoveImplementer => {
			return new WebDavMoveImplementer();
		},
		[]
	);

	const options: ItemPickerOptions = useMemo(
		() => {
			const { sharePoint, webDav } = documentsConfiguration();

			const sharePointOptions: SharePointItemPickerOptions = sharePoint.isConfigured
				? {
					isEnabled: true,
					shouldShowPersonalViews: true,
					availableDestination: sharePointMoveImplementer.getEnabledView(thingsToMove),
				}
				: { isEnabled: false };

			const webDavOptions: WebDavItemPickerOptions = webDav && webDav.isConfigured
				? {
					isEnabled: true,
					availableDestination: webDavMoveImplementer.getEnabledServer(thingsToMove)
				}
				: { isEnabled: false };

			const commonOptions: CommonItemPickerOptions = {
				mode: ItemPickerMode.SingleFolder,
				creationOptions: {
					folder: true
				}
			};

			return {
				sharePointOptions,
				webDavOptions,
				commonOptions
			};
		},
		[
			sharePointMoveImplementer,
			thingsToMove,
			webDavMoveImplementer
		]
	);

	const moveItemsHandler = useCallback(
		async (): Promise<void> => {
			try {
				setIsLoading(true);

				let result: MoveResult;
				switch (thingsToMove.storeKind) {
					case DocumentStoreType.SharePoint:
						result = await sharePointMoveImplementer.move(
							thingsToMove,
							convertToDestination(location),
							globalCancellation
						);
						break;
					case DocumentStoreType.WebDav:
						result = await webDavMoveImplementer.move(
							thingsToMove,
							convertToDestination(location),
							globalCancellation
						);
						break;
					default:
						never(thingsToMove);
				}

				switch (result.kind) {
					case "success":
						alertManager.showSuccess(localize("_FileMovedSuccessfully"));
						onClose();
						break;
					case "failed":
						setFailedItemsDialogState({
							isOpen: true,
							items: result.failedItems
						});

						setIsLoading(false);
						break;
					case "scheduled":
							alertManager.showInfo(
								<FormattedText
									text={localize("_StartedMovingAmountItemsToFolderNameSeeNotificationsForAdditionalInformation")}
									thingsToReplace={[
										{ what: "{{Amount}}", filler: thingsToMove.documents.length.toString() },
										{ what: "{{FolderName}}", filler: location.folder.name },
									]}
								/>
							);
						onClose();
						break;
					case "same-destination":
						// Nothing to do as the source and the destination are the same
						setIsLoading(false);
						break;
					default:
						never(result);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				setIsLoading(false);
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			location,
			sharePointMoveImplementer,
			thingsToMove,
			webDavMoveImplementer,
			onClose,
			globalCancellation
		]
	);

	return (
		<>
			<Dialog
				trackingId="move-document-dialog"
				hasBusyIndicator={isLoading}
				variant="left-slider"
				onClose={onClose}
			>
				<Dialog.Header
					title={localize("_MoveTo")}
					onClose={onClose}
					hasBorder
				/>
				<Dialog.Body goesEdgeToEdge>
					<ItemPicker
						options={options}
						location={location}
						onLocationChange={setLocation}
					/>
				</Dialog.Body>
				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							onClick={moveItemsHandler}
							text={localize("_MoveHere")}
							isDisabled={isButtonDisabled}
							trackingId="move-here"
						/>
						<Toolbar.Button
							kind="secondary"
							onClick={onClose}
							text={localize("_Cancel")}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{failedItemsDialogState.isOpen && (
				<NotAllItemsWereMovedDialog
					onClose={() => setFailedItemsDialogState({ isOpen: false })}
					failedItems={failedItemsDialogState.items}
				/>
			)}
		</>
	);
};

MoveDialog.displayName="MoveDialog";

type NotAllItemsWereMovedDialogProp = {
	failedItems: readonly MoveFailedItem[];
	onClose: () => void;
};

const NotAllItemsWereMovedDialog = (props: NotAllItemsWereMovedDialogProp): ReactElement => {
	const {
		failedItems,
		onClose
	} = props;

	return (
		<Dialog trackingId="not-all-items-were-moved-dialog">
			<Dialog.Header
				title={localize("_NotAllItemsWereMoved")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<p>{localize("_TheFollowingItemsCouldNotBeMoved")}</p>
				<ul>
					{failedItems.map((x: MoveFailedItem, i: number): ReactElement => (
						<li key={i}>{x.name}{x.reason && `: ${x.reason}`}</li>
					))}
				</ul>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={onClose}
						text={localize("_Close")}
						trackingId="close"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
NotAllItemsWereMovedDialog.displayName = "NotAllItemsWereMovedDialog";

type FailedItemsDialogState = {
	isOpen: false;
} | {
	isOpen: true;
	items: readonly MoveFailedItem[];
};

type DialogState = {
	isOpen: false;
} | {
	isOpen: true;
	thingsToMove: ThingsToMove;
};

type MoveDialogInitialData = Omit<MoveDialogProps, "onClose">;

export function useMoveDialog(): [
	dialogElement: ReactElement | null,
	open: (props: MoveDialogInitialData) => void
] {

	const [dialogState, setDialogState] = useState<DialogState>({isOpen: false});

	const renderDialog = useCallback(
		(): ReactElement | null => {
			return dialogState.isOpen
				? (
					<MoveDialog
						thingsToMove={dialogState.thingsToMove}
						onClose={() => setDialogState({ isOpen:false })}
					/>
				)
				: null;
		},
		[dialogState]
	);

	const handleOpenDialog = useCallback(
		(props: MoveDialogInitialData): void => {
			setDialogState({
				isOpen: true,
				thingsToMove: props.thingsToMove
			});
		},
		[]
	);

	return [
		renderDialog(),
		handleOpenDialog
	];
}

function getLocationUniqueId(location: ItemPickerLocation): string {
	switch (location.kind) {
		case DocumentStoreType.SharePoint:
			const sharePointViewId = location.folder
				.viewId;
			const sharePointFolderId = location.folder
				.id;

			return `SharePoint: ${sharePointViewId}_${sharePointFolderId}`;

		case DocumentStoreType.WebDav:
			const webDavFolderId = location.folder
				.id;
			const webDavServerId = location.folder
				.server
				.id;

			return `WebDav: ${webDavServerId}_${webDavFolderId}`;

		default:
			never(location);
	}
}